import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Users, FileText, Loader2 } from 'lucide-react';
import { searchAPI } from '../services/api';
import { toast } from 'sonner';

const Search = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    if (query.trim()) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    try {
      const response = await searchAPI.search(searchQuery);
      setUsers(response.data.users || []);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to perform search');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 pt-20">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">Search Results</h1>
          <p className="text-muted-foreground">
            {query ? `Showing results for "${query}"` : 'Enter a search query'}
          </p>
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="search-skeleton">
            <Skeleton className="h-10 w-full rounded-lg mb-4" />
            {[0, 1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Skeleton className="w-14 h-14 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Users ({users.length})
              </TabsTrigger>
              <TabsTrigger value="posts" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Posts ({posts.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="users">
              {users.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">No users found</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {users.map((user) => (
                    <Link
                      key={user._id}
                      to={`/profile/${user.username}`}
                      className="block"
                    >
                      <Card className="hover:bg-secondary/50 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="w-14 h-14">
                              <AvatarImage src={user.profileImage} alt={user.name} />
                              <AvatarFallback>{user.name?.[0] || 'U'}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <h3 className="font-semibold text-foreground">
                                {user.name}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                @{user.username}
                              </p>
                              {user.persona && (
                                <Badge variant="secondary" className="mt-1">
                                  {user.persona}
                                </Badge>
                              )}
                              {user.sports && user.sports.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {user.sports.join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="posts">
              {posts.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">No posts found</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <Card key={post._id}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={post.author?.profileImage} />
                            <AvatarFallback>{post.author?.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <Link
                              to={`/profile/${post.author?.username}`}
                              className="font-semibold hover:underline"
                            >
                              {post.author?.name}
                            </Link>
                            <p className="text-sm text-muted-foreground">
                              @{post.author?.username}
                            </p>
                          </div>
                        </div>
                        <p className="text-foreground mb-2">{post.content}</p>
                        {post.image && (
                          <img
                            src={post.image}
                            alt="Post"
                            className="w-full rounded-lg mb-2"
                          />
                        )}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{post.likes?.length || 0} likes</span>
                          <span>{post.comments?.length || 0} comments</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default Search;
